import { Alert, Button, Card, Form, Input, message } from 'antd';
import { useMutation } from '@tanstack/react-query';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import api from '@/services/api.service';
import { useAuthStore } from '@/store/authStore';
import EducationPageChrome from '@/pages/education/components/EducationPageChrome';
import { EducationSessionSelect } from '@/pages/education/components/EducationLookupSelects';

type FormValues = {
  session_id: string;
  title: string;
  theme_summary: string;
  goals_text?: string;
};

export default function AppPlanFormPage() {
  const { t } = useTranslation('education');
  const navigate = useNavigate();
  const user = useAuthStore((s) => s.user);
  const canManage = Boolean(
    user?.permissions?.includes('education.app.manage') || user?.permissions?.includes('education.admin')
  );

  const create = useMutation({
    mutationFn: (values: FormValues) => api.post<{ id: string }>('/education/app/plans', values),
    onSuccess: (res) => {
      message.success(t('appPlanCreated'));
      const id = res.data?.id;
      navigate(id ? `/education/app/${id}` : '/education/app');
    },
    onError: (error: { response?: { data?: { message?: string } } }) =>
      message.error(error.response?.data?.message || t('unavailable', { status: '' })),
  });

  if (!canManage) {
    return (
      <EducationPageChrome title={t('newAppPlan')}>
        <Alert type="warning" showIcon message={t('unavailable', { status: 403 })} />
      </EducationPageChrome>
    );
  }

  return (
    <EducationPageChrome
      title={t('newAppPlan')}
      description={t('appPlansIntro')}
      extra={<Button onClick={() => navigate('/education/app')}>{t('appPlans')}</Button>}
    >
      <Card size="small" style={{ maxWidth: 640 }}>
        <Alert type="info" showIcon style={{ marginBottom: 16 }} message={t('appBaselineHint')} />
        <Form layout="vertical" onFinish={(values) => create.mutate(values)}>
          <Form.Item name="session_id" label={t('session')} rules={[{ required: true }]}>
            <EducationSessionSelect />
          </Form.Item>
          <Form.Item name="title" label={t('title')} rules={[{ required: true }]}>
            <Input maxLength={200} />
          </Form.Item>
          <Form.Item name="theme_summary" label={t('themeSummary')} rules={[{ required: true }]}>
            <Input.TextArea rows={3} maxLength={1000} />
          </Form.Item>
          <Form.Item name="goals_text" label={t('goalsText')}>
            <Input.TextArea rows={4} maxLength={4000} />
          </Form.Item>
          <Button type="primary" htmlType="submit" loading={create.isPending}>
            {t('save')}
          </Button>
        </Form>
      </Card>
    </EducationPageChrome>
  );
}
